import { addEdge, Connection, Edge, Node, OnConnectStartParams } from "@xyflow/react";
import Interaction, { InteractionType } from "../../models/interactions/Interaction";
import { SequenceNodeData } from "./SequenceNode";
import { InteractionDefintion } from "../interaction_picker/InteractionDefinitions";
import { getInteractionFromFactory } from "../interaction_factory/InteractionFactory";
import { applySingleConnectionRules } from "./SequenceCanvasConnectionRules";
import { createSequenceEdgeId } from "./SequenceEdgeIds";

export function createSequenceNode(
  definition: InteractionDefintion,
  position: { x: number; y: number },
  getArrayOfKeys: (amount: number) => string[]
): Node<SequenceNodeData> {
  const newNodeInteraction = getInteractionFromFactory(definition.type).Create(
    getArrayOfKeys
  ) as Interaction;
  if (definition.createParams?.operator != null) {
    (newNodeInteraction as Interaction & { operator: string }).operator =
      definition.createParams.operator;
  }

  const newNodeId = `node-${Date.now()}`;
  return {
    id: newNodeId,
    type: "sequence",
    dragHandle: ".draggable-header",
    position,
    data: {
      interaction: newNodeInteraction,
      name: "New Node",
      id: newNodeId,
    },
  };
}

type BuildEdgesForAddedNodeParams = {
  existingEdges: Edge[];
  newNodeId: string;
  edgeToSplit: Edge | null;
  pendingConnectionSource: OnConnectStartParams | null;
  nodeTypeById: Map<string, InteractionType>;
};

export function buildEdgesForAddedNode({
  existingEdges,
  newNodeId,
  edgeToSplit,
  pendingConnectionSource,
  nodeTypeById,
}: BuildEdgesForAddedNodeParams): Edge[] {
  let newEdges = [...existingEdges];

  if (edgeToSplit) {
    newEdges = newEdges.filter((edge) => edge.id !== edgeToSplit.id);

    const edge1: Edge = {
      id: createSequenceEdgeId(edgeToSplit.source, newNodeId),
      source: edgeToSplit.source,
      sourceHandle: edgeToSplit.sourceHandle || null,
      target: newNodeId,
      targetHandle: null,
      type: "custom",
    };

    const edge2: Edge = {
      id: createSequenceEdgeId(newNodeId, edgeToSplit.target),
      source: newNodeId,
      sourceHandle: null,
      target: edgeToSplit.target,
      targetHandle: edgeToSplit.targetHandle || null,
      type: "custom",
    };

    return [...newEdges, edge1, edge2];
  }

  if (!pendingConnectionSource?.nodeId) {
    return newEdges;
  }

  let connection: Connection;
  if (pendingConnectionSource.handleType === "source") {
    connection = {
      source: pendingConnectionSource.nodeId,
      sourceHandle: pendingConnectionSource.handleId || null,
      target: newNodeId,
      targetHandle: null,
    };
  } else {
    connection = {
      source: newNodeId,
      sourceHandle: null,
      target: pendingConnectionSource.nodeId,
      targetHandle: pendingConnectionSource.handleId || null,
    };
  }

  const filteredEdges = applySingleConnectionRules(
    newEdges,
    connection,
    nodeTypeById
  );
  return addEdge({ ...connection, type: "custom" }, filteredEdges);
}
